<template>
  <transition name="tad-slide">
    <div v-if="open" class="tad-overlay" @click.self="close">
      <div class="tad-drawer" role="dialog" aria-modal="true">
        <header class="tad-header">
          <div class="tad-header-info">
            <span class="tad-eyebrow">DETALHE DO LOCAL</span>
            <h3 class="tad-title">{{ tagName }}</h3>
            <div v-if="hasFeatures" class="tad-features">
              <span v-if="features.has_demonstration" class="tad-feat tad-feat-demo" title="Demonstração">
                <i class="bx bx-tv"></i>
              </span>
              <span v-if="features.has_activation" class="tad-feat tad-feat-activation" title="Ativação">
                <i class="bx bx-target-lock"></i>
              </span>
              <span v-if="features.has_food" class="tad-feat tad-feat-food" title="Café/comida">
                <i class="bx bx-coffee"></i>
              </span>
              <span v-if="features.has_giveaway" class="tad-feat tad-feat-gift" title="Brinde">
                <i class="bx bx-gift"></i>
              </span>
              <span v-if="features.queue_minutes > 0" class="tad-feat tad-feat-queue" :title="`Fila ~${features.queue_minutes}min`">
                <i class="bx bx-loader-circle"></i>
              </span>
            </div>
          </div>
          <button type="button" class="tad-close" @click="close" aria-label="Fechar">
            <i class="bx bx-x"></i>
          </button>
        </header>

        <div v-if="isLoading && !hasContent" class="tad-loading">
          <div class="tad-skel tad-skel-kpi"></div>
          <div class="tad-skel tad-skel-chart"></div>
          <div class="tad-skel tad-skel-list"></div>
        </div>

        <div v-else class="tad-body">
          <!-- KPIs -->
          <div class="tad-kpi-grid">
            <div class="tad-kpi">
              <div class="tad-kpi-label">Pessoas que passaram</div>
              <div class="tad-kpi-value tad-kpi-value-primary">{{ kpis.total_passed || 0 }}</div>
              <div class="tad-kpi-sub">{{ kpis.total_reads || 0 }} leituras totais</div>
            </div>
            <div class="tad-kpi">
              <div class="tad-kpi-label">{{ isFairLike ? 'Estimadas agora' : 'Dentro agora' }}</div>
              <div class="tad-kpi-value">
                <span v-if="kpis.estimated_now !== null && kpis.estimated_now !== undefined">
                  <span class="tad-kpi-prefix">≈</span>{{ kpis.estimated_now }}
                </span>
                <span v-else>{{ kpis.current_inside || 0 }}</span>
              </div>
              <div class="tad-kpi-sub" v-if="estimation">
                {{ estimation.dwell_minutes }} min · {{ estimation.check_ins_per_min }}/min
              </div>
            </div>
            <div class="tad-kpi">
              <div class="tad-kpi-label">Pico</div>
              <div class="tad-kpi-value">
                <span v-if="kpis.peak_hour">{{ kpis.peak_hour }}</span>
                <span v-else>—</span>
                <span v-if="kpis.peak_hour_end" class="tad-kpi-suffix">– {{ kpis.peak_hour_end }}</span>
              </div>
              <div class="tad-kpi-sub">{{ kpis.peak_count || 0 }} check-ins</div>
            </div>
            <div class="tad-kpi">
              <div class="tad-kpi-label">Janela analisada</div>
              <div class="tad-kpi-value tad-kpi-value-small">
                <span>{{ formatShortDate(kpis.first_read_at) }}</span>
                <span class="tad-kpi-sep">→</span>
                <span>{{ formatShortDate(kpis.last_read_at) }}</span>
              </div>
              <div class="tad-kpi-sub">{{ formatRelativeWindow() }}</div>
            </div>
          </div>

          <!-- Timeline -->
          <section class="tad-section">
            <header class="tad-section-head">
              <h4 class="tad-section-title">Fluxo no local</h4>
              <div class="tad-bucket-chips">
                <button
                  v-for="b in bucketOptions"
                  :key="b"
                  type="button"
                  class="tad-bchip"
                  :class="{ 'tad-bchip-active': bucket === b }"
                  @click="changeBucket(b)"
                >{{ b }}</button>
              </div>
            </header>
            <div v-if="!hasTimeline" class="tad-empty-mini">
              Sem leituras no período
            </div>
            <apexchart
              v-else
              type="area"
              height="220"
              :options="timelineOptions"
              :series="timelineSeries"
            />
          </section>

          <!-- Hourly distribution -->
          <section class="tad-section" v-if="hasHourly">
            <header class="tad-section-head">
              <h4 class="tad-section-title">Distribuição por hora</h4>
              <span class="tad-section-hint">Acumulado da janela</span>
            </header>
            <apexchart
              type="bar"
              height="180"
              :options="hourlyOptions"
              :series="hourlySeries"
            />
          </section>

          <!-- Perfil dos visitantes — análise por campo escopada nesta tag.
               Mesma UX do FieldAnalysisCard global: placeholder → modal de
               campos → ranking. Diferença: aqui o universo é só quem visitou
               este local. -->
          <section class="tad-section">
            <header class="tad-section-head">
              <h4 class="tad-section-title">Perfil dos visitantes</h4>
              <div class="tad-section-actions">
                <span v-if="fieldSelected" class="tad-field-chip">{{ fieldSelectedLabel }}</span>
                <span v-if="fieldSecondary" class="tad-field-chip-cross">
                  <i class="bx bx-x"></i>
                </span>
                <span v-if="fieldSecondary" class="tad-field-chip tad-field-chip-secondary">
                  {{ fieldSecondaryLabel }}
                </span>
                <button
                  v-if="fieldSelected"
                  class="tad-field-change"
                  @click="openPrimaryPicker"
                  title="Trocar campo principal"
                >
                  <i class="bx bx-transfer-alt"></i>
                </button>
              </div>
            </header>

            <div v-if="!fieldSelected" class="tad-field-placeholder" @click="openPrimaryPicker">
              <i class="bx bx-stats"></i>
              <div class="tad-field-placeholder-text">Quem mais visitou este local?</div>
              <div class="tad-field-placeholder-sub">
                Escolha um campo (cargo, grupo, cidade…) pra ver a distribuição
                dos visitantes.
              </div>
            </div>

            <div v-else-if="fieldLoading" class="tad-field-loading">
              <i class="bx bx-loader-alt tad-spin"></i>
              <span>Cruzando dados…</span>
            </div>

            <div v-else-if="fieldRows.length === 0" class="tad-field-empty">
              <i class="bx bx-data"></i>
              <p>Nenhum visitante com este campo preenchido</p>
            </div>

            <!-- Single mode: ranking simples -->
            <div v-else-if="!fieldSecondary" class="tad-field-ranking">
              <div class="tad-field-meta-row">
                <span class="tad-field-meta">
                  <i class="bx bx-group"></i>
                  {{ fieldData.total }} respostas entre os visitantes
                </span>
                <button
                  class="tad-field-cross-btn"
                  @click="openSecondaryPicker"
                  title="Cruzar com outro campo"
                >
                  <i class="bx bx-shuffle"></i>
                  Cruzar com…
                </button>
              </div>
              <div
                v-for="(row, i) in fieldRows.slice(0, 10)"
                :key="row.value + i"
                class="tad-field-row"
              >
                <div class="tad-field-num" :class="fieldRankClass(i)">{{ i + 1 }}</div>
                <div class="tad-field-body">
                  <div class="tad-field-label" :title="row.value">{{ row.value }}</div>
                  <div class="tad-field-bar">
                    <div
                      class="tad-field-fill"
                      :style="{ width: fieldBarWidth(row.count) }"
                    ></div>
                  </div>
                </div>
                <div class="tad-field-right">
                  <span class="tad-field-count">{{ row.count }}</span>
                  <span class="tad-field-pct">{{ fieldPct(row.count) }}%</span>
                </div>
              </div>
            </div>

            <!-- Cross mode: hierárquico (primary > secondary) -->
            <div v-else class="tad-field-cross-list">
              <div class="tad-field-meta-row">
                <span class="tad-field-meta">
                  <i class="bx bx-group"></i>
                  {{ fieldData.total }} em {{ fieldGroupedRows.length }}
                  {{ fieldGroupedRows.length === 1 ? 'grupo' : 'grupos' }}
                </span>
                <button
                  class="tad-field-cross-btn tad-field-cross-btn-active"
                  @click="onClearFieldSecondary"
                  title="Voltar para visão simples"
                >
                  <i class="bx bx-x"></i>
                  Remover cruzamento
                </button>
              </div>

              <div
                v-for="(group, gIdx) in fieldGroupedRows"
                :key="group.primary + gIdx"
                class="tad-field-cross-group"
              >
                <div class="tad-field-cross-head">
                  <span class="tad-field-num" :class="fieldRankClass(gIdx)">{{ gIdx + 1 }}</span>
                  <span class="tad-field-cross-name" :title="group.primary">{{ group.primary }}</span>
                  <span class="tad-field-cross-count">{{ group.total }}</span>
                  <span class="tad-field-cross-pct">{{ fieldGroupPct(group.total) }}%</span>
                </div>
                <div class="tad-field-cross-children">
                  <div
                    v-for="(child, cIdx) in group.items.slice(0, 5)"
                    :key="child.secondary_value + cIdx"
                    class="tad-field-cross-child"
                  >
                    <span class="tad-field-cross-child-label" :title="child.secondary_value">{{ child.secondary_value }}</span>
                    <div class="tad-field-cross-child-bar">
                      <div
                        class="tad-field-cross-child-fill"
                        :style="{ width: fieldBarWidthRel(child.count, group.max) }"
                      ></div>
                    </div>
                    <span class="tad-field-cross-child-count">{{ child.count }}</span>
                  </div>
                  <div v-if="group.items.length > 5" class="tad-field-cross-more">
                    + {{ group.items.length - 5 }} {{ group.items.length - 5 === 1 ? 'outro' : 'outros' }}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Top visitors -->
          <section class="tad-section" v-if="topVisitors && topVisitors.length > 0">
            <header class="tad-section-head">
              <h4 class="tad-section-title">Top visitantes</h4>
              <span class="tad-section-hint">Quem mais passou</span>
            </header>
            <div class="tad-visitor-list">
              <div
                v-for="(v, idx) in topVisitors.slice(0, 10)"
                :key="v.participant_id + ':' + idx"
                class="tad-visitor-row"
              >
                <span class="tad-visitor-rank">{{ idx + 1 }}</span>
                <span class="tad-visitor-name" :title="v.name">{{ v.name || 'Sem nome' }}</span>
                <span class="tad-visitor-visits">{{ v.visits }}× </span>
                <span class="tad-visitor-time">última {{ formatRelative(v.last_visit) }}</span>
              </div>
            </div>
          </section>

          <!-- Recent visitors -->
          <section class="tad-section" v-if="recentVisitors && recentVisitors.length > 0">
            <header class="tad-section-head">
              <h4 class="tad-section-title">Leituras recentes</h4>
              <span class="tad-section-hint">{{ recentVisitors.length }} mais novas</span>
            </header>
            <div class="tad-recent-list">
              <div
                v-for="r in recentVisitors"
                :key="r.id"
                class="tad-recent-row"
                :class="`tad-recent-${r.event_type === 'CheckedIn' ? 'in' : 'out'}`"
              >
                <span class="tad-recent-icon">
                  <i :class="r.event_type === 'CheckedIn' ? 'bx bx-log-in-circle' : 'bx bx-log-out-circle'"></i>
                </span>
                <span class="tad-recent-name" :title="r.name">{{ r.name || 'Anônimo' }}</span>
                <span class="tad-recent-time">{{ formatTimeOnly(r.event_date) }}</span>
              </div>
            </div>
          </section>
        </div>

        <!-- Modal picker de campos (overlay sobre o drawer) — usado tanto pra
             primary quanto secondary, controlado por pickerMode -->
        <div v-if="showFieldPicker" class="tad-picker-overlay" @click.self="closeFieldPicker">
          <div class="tad-picker">
            <div class="tad-picker-header">
              <div class="tad-picker-header-left">
                <i class="bx bx-search-alt-2 tad-picker-header-icon"></i>
                <span class="tad-picker-title">{{ pickerTitle }}</span>
              </div>
              <button class="tad-picker-close" @click="closeFieldPicker">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <div v-if="pickerMode === 'secondary' && fieldSelected" class="tad-picker-context">
              <span class="tad-picker-context-label">Cruzando com</span>
              <span class="tad-field-chip tad-picker-context-chip">{{ fieldSelectedLabel }}</span>
            </div>

            <div class="tad-picker-body">
              <div
                v-for="group in pickerFieldsByGroup"
                :key="group.name"
                class="tad-picker-group"
              >
                <div class="tad-picker-group-label">{{ group.name }}</div>
                <div class="tad-picker-fields">
                  <button
                    v-for="f in group.items"
                    :key="fieldKey(f)"
                    class="tad-picker-field"
                    :class="{ selected: isFieldSelectedInPicker(f) }"
                    @click="onPickField(f)"
                  >
                    <span>{{ f.label }}</span>
                    <i v-if="isFieldSelectedInPicker(f)" class="bx bx-check"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="hasSelectionInPicker" class="tad-picker-footer">
              <button class="tad-picker-clear" @click="onClearInPicker">
                <i class="bx bx-trash"></i>
                {{ pickerMode === 'secondary' ? 'Remover cruzamento' : 'Limpar seleção' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'tag-analytics-drawer',
  props: {
    open: { type: Boolean, default: false },
    detail: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    bucket: { type: String, default: '1h' },
    isFair: { type: Boolean, default: false },
    availableFields: { type: Array, default: () => [] },
    fieldSelected: { type: Object, default: null },
    fieldSecondary: { type: Object, default: null },
    fieldData: { type: Object, default: null },
    fieldLoading: { type: Boolean, default: false }
  },
  data: () => ({
    bucketOptions: ['5min', '15min', '1h', '1d'],
    showFieldPicker: false,
    pickerMode: 'primary' // 'primary' | 'secondary'
  }),
  watch: {
    /**
     * Bloqueia scroll do body quando drawer abre + reset do field picker
     * sempre que o drawer fecha.
     */
    open (val) {
      document.body.style.overflow = val ? 'hidden' : ''
      if (!val) this.showFieldPicker = false
    }
  },
  computed: {
    hasContent () {
      return this.detail && this.detail.kpis && Object.keys(this.detail.kpis).length > 0
    },
    tagName () {
      return this.detail && this.detail.tag ? this.detail.tag.name : '—'
    },
    features () {
      return this.detail && this.detail.tag && this.detail.tag.features ? this.detail.tag.features : {}
    },
    hasFeatures () {
      const f = this.features
      return f.has_demonstration || f.has_activation || f.has_food || f.has_giveaway || f.queue_minutes > 0
    },
    kpis () {
      return (this.detail && this.detail.kpis) || {}
    },
    estimation () {
      return this.detail && this.detail.estimation
    },
    isFairLike () {
      return this.isFair || (this.estimation !== null && this.estimation !== undefined)
    },
    timeline () {
      return (this.detail && this.detail.timeline) || []
    },
    hasTimeline () {
      return this.timeline.length > 0
    },
    timelineSeries () {
      const ins = this.timeline.map(b => [new Date(b.bucket).getTime(), b.in || 0])
      const outs = this.timeline.map(b => [new Date(b.bucket).getTime(), b.out || 0])
      const series = [{ name: 'Entradas', data: ins }]
      if (outs.some(p => p[1] > 0)) series.push({ name: 'Saídas', data: outs })
      return series
    },
    timelineOptions () {
      return {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
        colors: ['#34c38f', '#f1b44c'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] }
        },
        markers: { size: 0, hover: { size: 4 } },
        xaxis: {
          type: 'datetime',
          labels: { datetimeUTC: false, style: { colors: '#8b90a5', fontSize: '10px' } },
          axisBorder: { show: false }
        },
        yaxis: { labels: { style: { colors: '#8b90a5', fontSize: '10px' } } },
        grid: { borderColor: '#eaecf2', strokeDashArray: 4 },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px' },
        tooltip: { theme: 'light', x: { format: 'dd/MM HH:mm' } }
      }
    },
    hasHourly () {
      return this.detail && this.detail.hourly_distribution &&
        this.detail.hourly_distribution.some(h => h.count > 0)
    },
    hourlySeries () {
      return [{
        name: 'Check-ins',
        data: (this.detail.hourly_distribution || []).map(h => h.count)
      }]
    },
    hourlyOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#556ee6'],
        plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.detail.hourly_distribution || []).map(h => h.hour),
          labels: { style: { colors: '#8b90a5', fontSize: '9px' } },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { labels: { style: { colors: '#8b90a5', fontSize: '10px' } } },
        grid: { borderColor: '#eaecf2', strokeDashArray: 4 }
      }
    },
    topVisitors () {
      return (this.detail && this.detail.top_visitors) || []
    },
    recentVisitors () {
      return (this.detail && this.detail.recent_visitors) || []
    },
    fieldsByGroup () {
      return this.groupFields(this.availableFields)
    },
    pickerFieldsByGroup () {
      // No modo secondary, exclui o campo primário da lista (cruzar consigo
      // mesmo não faz sentido)
      if (this.pickerMode === 'secondary' && this.fieldSelected) {
        const filtered = this.availableFields.filter(
          f => !(f.source === this.fieldSelected.source &&
                 String(f.id) === String(this.fieldSelected.id))
        )
        return this.groupFields(filtered)
      }
      return this.fieldsByGroup
    },
    pickerTitle () {
      return this.pickerMode === 'secondary'
        ? 'Cruzar com qual campo?'
        : 'Escolher campo para análise'
    },
    hasSelectionInPicker () {
      return this.pickerMode === 'secondary' ? !!this.fieldSecondary : !!this.fieldSelected
    },
    fieldSelectedLabel () {
      if (!this.fieldSelected) return ''
      const f = this.availableFields.find(
        x => x.source === this.fieldSelected.source &&
        String(x.id) === String(this.fieldSelected.id)
      )
      return f ? f.label : ''
    },
    fieldSecondaryLabel () {
      if (!this.fieldSecondary) return ''
      const f = this.availableFields.find(
        x => x.source === this.fieldSecondary.source &&
        String(x.id) === String(this.fieldSecondary.id)
      )
      return f ? f.label : ''
    },
    fieldRows () {
      return (this.fieldData && this.fieldData.rows) || []
    },
    fieldChartMax () {
      return this.fieldRows[0] ? this.fieldRows[0].count || 1 : 1
    },
    /**
     * Cross mode: agrupa rows por valor primário (já vem ordenado por count
     * desc do backend). Cada grupo tem max próprio pra que as barras dos
     * children sejam relativas dentro do grupo.
     */
    fieldGroupedRows () {
      const map = new Map()
      const groups = []
      this.fieldRows.forEach(row => {
        const key = row.primary_value
        let g = map.get(key)
        if (!g) {
          g = { primary: key, items: [], total: 0, max: 0 }
          map.set(key, g)
          groups.push(g)
        }
        g.items.push(row)
        g.total += row.count
        if (row.count > g.max) g.max = row.count
      })
      groups.sort((a, b) => b.total - a.total)
      return groups.slice(0, 6)
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
  },
  methods: {
    close () {
      this.$emit('close')
    },
    changeBucket (b) {
      this.$emit('change-bucket', b)
    },
    formatShortDate (datetime) {
      if (!datetime) return '—'
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      const day = String(d.getDate()).padStart(2, '0')
      const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
      const m = months[d.getMonth()]
      const time = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      return `${day}/${m} ${time}`
    },
    formatTimeOnly (datetime) {
      if (!datetime) return ''
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
    },
    formatRelative (datetime) {
      if (!datetime) return '—'
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000))
      if (diff < 1) return 'agora'
      if (diff < 60) return `há ${diff}min`
      const h = Math.floor(diff / 60)
      if (h < 24) return `há ${h}h`
      return `há ${Math.floor(h / 24)}d`
    },
    formatRelativeWindow () {
      const k = this.kpis
      if (!k.first_read_at || !k.last_read_at) return '—'
      const a = new Date(k.first_read_at.replace ? k.first_read_at.replace(' ', 'T') : k.first_read_at)
      const b = new Date(k.last_read_at.replace ? k.last_read_at.replace(' ', 'T') : k.last_read_at)
      const diffMin = Math.max(1, Math.round((b.getTime() - a.getTime()) / 60000))
      if (diffMin < 60) return `${diffMin} min de cobertura`
      const h = Math.floor(diffMin / 60)
      if (h < 24) return `${h}h de cobertura`
      return `${Math.floor(h / 24)} dia(s) de cobertura`
    },
    fieldKey (f) {
      return f ? `${f.source}::${f.id}` : ''
    },
    groupFields (fields) {
      const groups = new Map()
      fields.forEach(f => {
        const g = f.group || 'Outros'
        if (!groups.has(g)) groups.set(g, { name: g, items: [] })
        groups.get(g).items.push(f)
      })
      return Array.from(groups.values())
    },
    isFieldSelectedInPicker (f) {
      const target = this.pickerMode === 'secondary' ? this.fieldSecondary : this.fieldSelected
      return target &&
        target.source === f.source &&
        String(target.id) === String(f.id)
    },
    openPrimaryPicker () {
      this.pickerMode = 'primary'
      this.showFieldPicker = true
    },
    openSecondaryPicker () {
      this.pickerMode = 'secondary'
      this.showFieldPicker = true
    },
    closeFieldPicker () {
      this.showFieldPicker = false
    },
    onPickField (f) {
      const ref = { source: f.source, id: f.id }
      if (this.pickerMode === 'secondary') {
        this.$emit('change-field-secondary', ref)
      } else {
        this.$emit('change-field', ref)
      }
      this.showFieldPicker = false
    },
    onClearInPicker () {
      if (this.pickerMode === 'secondary') {
        this.$emit('change-field-secondary', null)
      } else {
        this.$emit('change-field', null)
      }
      this.showFieldPicker = false
    },
    onClearFieldSecondary () {
      this.$emit('change-field-secondary', null)
    },
    fieldRankClass (i) {
      if (i === 0) return 'tad-field-num--gold'
      if (i === 1) return 'tad-field-num--silver'
      if (i === 2) return 'tad-field-num--bronze'
      return ''
    },
    fieldBarWidth (count) {
      const pct = Math.max(2, Math.round((count / this.fieldChartMax) * 100))
      return `${pct}%`
    },
    fieldBarWidthRel (count, max) {
      const pct = Math.max(2, Math.round((count / Math.max(1, max)) * 100))
      return `${pct}%`
    },
    fieldPct (count) {
      const total = this.fieldData && this.fieldData.total ? this.fieldData.total : 1
      return Math.round((count / total) * 100)
    },
    fieldGroupPct (groupTotal) {
      const total = this.fieldData && this.fieldData.total ? this.fieldData.total : 1
      return Math.round((groupTotal / total) * 100)
    }
  }
}
</script>

<style lang="scss" scoped>
.tad-overlay {
  position: fixed;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.tad-drawer {
  width: 560px;
  max-width: 100vw;
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -16px 0 40px rgba(26, 29, 38, 0.2);
  overflow: hidden;
  position: relative; /* contém o overlay do field picker */
}

/* Slide-from-right transition */
.tad-slide-enter-active,
.tad-slide-leave-active {
  transition: opacity 0.25s ease;
}
.tad-slide-enter-active .tad-drawer,
.tad-slide-leave-active .tad-drawer {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.tad-slide-enter,
.tad-slide-leave-to {
  opacity: 0;
}
.tad-slide-enter .tad-drawer,
.tad-slide-leave-to .tad-drawer {
  transform: translateX(100%);
}

/* Header */
.tad-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px;
  border-bottom: 1px solid #eaecf2;
  flex-shrink: 0;
}

.tad-header-info { min-width: 0; }

.tad-eyebrow {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8b90a5;
}

.tad-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1d26;
  margin: 4px 0 8px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
}

.tad-features {
  display: inline-flex;
  gap: 4px;
}

.tad-feat {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;

  i { font-size: 14px; }

  &.tad-feat-demo       { background: rgba(85, 110, 230, 0.12); color: #556ee6; }
  &.tad-feat-activation { background: rgba(139, 92, 246, 0.12); color: #8b5cf6; }
  &.tad-feat-food       { background: rgba(241, 180, 76, 0.16); color: #b8801f; }
  &.tad-feat-gift       { background: rgba(236, 72, 153, 0.12); color: #ec4899; }
  &.tad-feat-queue      { background: rgba(244, 106, 106, 0.12); color: #d04848; }
}

.tad-close {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #f5f6fa;
  border: 0;
  border-radius: 8px;
  color: #4a5068;
  cursor: pointer;
  font-size: 22px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 106, 106, 0.1);
    color: #d04848;
  }
}

/* Body */
.tad-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 26px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
}

.tad-loading {
  flex: 1;
  padding: 22px 26px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tad-skel {
  background: linear-gradient(90deg, #f0f1f6 0%, #f8f9fc 50%, #f0f1f6 100%);
  background-size: 200% 100%;
  border-radius: 12px;
  animation: tad-shimmer 1.6s ease-in-out infinite;
}
.tad-skel-kpi { height: 90px; }
.tad-skel-chart { height: 240px; }
.tad-skel-list { height: 160px; }

@keyframes tad-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* KPI grid */
.tad-kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 20px;
}

.tad-kpi {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 12px 14px;
}

.tad-kpi-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8b90a5;
  margin-bottom: 6px;
}

.tad-kpi-value {
  font-size: 26px;
  font-weight: 800;
  color: #1a1d26;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: 4px;

  &.tad-kpi-value-primary { color: #556ee6; }
  &.tad-kpi-value-small { font-size: 14px; gap: 6px; }
}

.tad-kpi-prefix {
  font-size: 18px;
  font-weight: 500;
  color: #8b90a5;
}

.tad-kpi-suffix {
  font-size: 13px;
  color: #8b5cf6;
  font-weight: 600;
}

.tad-kpi-sub {
  font-size: 11px;
  color: #8b90a5;
  margin-top: 4px;
  font-weight: 500;
}

.tad-kpi-sep {
  color: #d0d5dd;
  font-weight: 400;
}

/* Sections */
.tad-section {
  margin-bottom: 22px;
}

.tad-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.tad-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0;
}

.tad-section-hint {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
}

.tad-section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Análise por campo dentro do drawer — escopo: visitantes deste local */
.tad-field-chip {
  padding: 2px 10px;
  background: rgba(85, 110, 230, 0.08);
  border: 1px solid rgba(85, 110, 230, 0.2);
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #556ee6;
}

.tad-field-change {
  width: 28px;
  height: 28px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  background: #fff;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 14px;
  transition: all 0.15s ease;

  &:hover {
    color: #556ee6;
    border-color: #556ee6;
  }
}

.tad-field-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  border: 2px dashed #d0d4e8;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    border-color: #556ee6;
    background: rgba(85, 110, 230, 0.03);
  }

  i {
    font-size: 28px;
    color: #556ee6;
    margin-bottom: 10px;
  }
}

.tad-field-placeholder-text {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.tad-field-placeholder-sub {
  font-size: 12px;
  color: #8b90a5;
  max-width: 320px;
  line-height: 1.5;
}

.tad-field-loading,
.tad-field-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px;
  color: #8b90a5;
  font-size: 13px;

  p { margin: 0; font-size: 12px; }
  i { font-size: 18px; color: #b0b5c8; }
}

.tad-spin {
  color: #556ee6 !important;
  animation: tad-spin 0.9s linear infinite;
}

@keyframes tad-spin {
  to { transform: rotate(360deg); }
}

.tad-field-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.tad-field-meta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  background: #f5f6fa;
  color: #4a5068;

  i { color: #8b90a5; font-size: 13px; }
}

.tad-field-cross-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px dashed rgba(139, 92, 246, 0.4);
  border-radius: 5px;
  background: transparent;
  color: #8b5cf6;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  i { font-size: 13px; }

  &:hover {
    background: rgba(139, 92, 246, 0.06);
    border-style: solid;
  }

  &.tad-field-cross-btn-active {
    background: rgba(139, 92, 246, 0.08);
    border-style: solid;
  }
}

.tad-field-chip-cross {
  color: #b0b5c8;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
}

.tad-field-chip-secondary {
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.25);
  color: #8b5cf6;
}

.tad-field-ranking {
  display: flex;
  flex-direction: column;
}

/* Cross view (hierárquico) — versão compacta pro drawer */
.tad-field-cross-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tad-field-cross-group {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 8px;
  padding: 10px 12px;
}

.tad-field-cross-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f1f6;
}

.tad-field-cross-name {
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tad-field-cross-count {
  font-size: 13px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
}

.tad-field-cross-pct {
  font-size: 10px;
  font-weight: 600;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.08);
  padding: 1px 5px;
  border-radius: 4px;
}

.tad-field-cross-children {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.tad-field-cross-child {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.tad-field-cross-child-label {
  flex: 0 0 38%;
  color: #4a5068;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tad-field-cross-child-bar {
  flex: 1;
  height: 4px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
}

.tad-field-cross-child-fill {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 99px;
}

.tad-field-cross-child-count {
  font-size: 11px;
  font-weight: 700;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  min-width: 24px;
  text-align: right;
}

.tad-field-cross-more {
  font-size: 10px;
  color: #8b90a5;
  font-style: italic;
  padding: 2px 0 0;
}

.tad-field-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.tad-field-num {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: #f5f6fa;
  color: #b0b5c8;

  &.tad-field-num--gold   { background: #fef3c7; color: #d97706; }
  &.tad-field-num--silver { background: #f1f5f9; color: #64748b; }
  &.tad-field-num--bronze { background: #fef0e6; color: #c2692c; }
}

.tad-field-body {
  flex: 1;
  min-width: 0;
}

.tad-field-label {
  font-size: 12px;
  font-weight: 500;
  color: #1a1d26;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tad-field-bar {
  height: 5px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
}

.tad-field-fill {
  height: 100%;
  background: linear-gradient(90deg, #556ee6, #7c8ff5);
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.tad-field-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 50px;
}

.tad-field-count {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
}

.tad-field-pct {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
}

/* Modal picker (overlay sobre o drawer) */
.tad-picker-overlay {
  position: absolute;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.tad-picker {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 60px rgba(26, 29, 38, 0.22);
  width: 100%;
  max-width: 480px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tad-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f3f5;
  background: #fafbfc;
}

.tad-picker-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tad-picker-header-icon {
  font-size: 16px;
  color: #556ee6;
}

.tad-picker-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
}

.tad-picker-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f5f6fa;
  border: none;
  font-size: 18px;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;

  &:hover { background: #eaecf2; }
}

.tad-picker-body {
  overflow-y: auto;
  padding: 14px 18px;
}

.tad-picker-group {
  margin-bottom: 16px;

  &:last-child { margin-bottom: 0; }
}

.tad-picker-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b90a5;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f3f5;
}

.tad-picker-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.tad-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 1.5px solid #eaecf2;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #4a5068;
  transition: all 0.12s ease;
  text-align: left;

  &:hover {
    background: #f5f6ff;
    border-color: #c5caf5;
  }

  &.selected {
    background: rgba(85, 110, 230, 0.07);
    border-color: rgba(85, 110, 230, 0.35);
    color: #3a4fa3;
  }

  i { font-size: 14px; color: #556ee6; }

  span {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.tad-picker-footer {
  padding: 10px 18px;
  border-top: 1px solid #f1f3f5;
}

.tad-picker-context {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: rgba(85, 110, 230, 0.04);
  border-bottom: 1px solid #f1f3f5;
}

.tad-picker-context-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
}

.tad-picker-context-chip {
  margin: 0;
}

.tad-picker-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 7px 10px;
  border: 1.5px dashed #e0b4b4;
  border-radius: 5px;
  background: #fff8f8;
  color: #c0392b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #fef0f0;
    border-color: #c0392b;
  }
}

.tad-bucket-chips {
  display: inline-flex;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 2px;
  gap: 1px;
}

.tad-bchip {
  background: transparent;
  border: 0;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  &:hover { background: #fff; }
  &.tad-bchip-active {
    background: #fff;
    color: #556ee6;
    box-shadow: 0 1px 3px rgba(26, 29, 38, 0.08);
  }
}

.tad-empty-mini {
  padding: 32px 16px;
  text-align: center;
  font-size: 12px;
  color: #8b90a5;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px dashed #eaecf2;
}

/* Visitor lists */
.tad-visitor-list,
.tad-recent-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tad-visitor-row,
.tad-recent-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  transition: background 0.15s ease;

  &:hover { background: #fafbfc; }
}

.tad-visitor-rank {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  font-size: 11px;
  font-weight: 700;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.tad-visitor-name {
  flex: 1;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tad-visitor-visits {
  font-weight: 700;
  color: #556ee6;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.tad-visitor-time {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
  flex-shrink: 0;
}

.tad-recent-icon {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  flex-shrink: 0;
  font-size: 14px;
}

.tad-recent-in .tad-recent-icon {
  background: rgba(52, 195, 143, 0.12);
  color: #34c38f;
}

.tad-recent-out .tad-recent-icon {
  background: rgba(241, 180, 76, 0.12);
  color: #f1b44c;
}

.tad-recent-name {
  flex: 1;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tad-recent-time {
  font-size: 11px;
  font-weight: 700;
  color: #4a5068;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .tad-drawer { width: 100vw; }
  .tad-kpi-grid { grid-template-columns: 1fr; }
}
</style>
